import React from 'react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

/**
 * GuardedButton Component
 * Automatically disables button based on subscription status
 * Shows tooltip explaining why button is disabled
 * 
 * Props:
 * - requireWrite: true = disabled for unpaid/suspended (default)
 * - requireAccess: true = disabled only for suspended
 * - ...buttonProps = all other Button props
 */
export const GuardedButton = ({ 
  children, 
  requireWrite = true,
  requireAccess = false,
  disabled = false,
  onClick,
  ...buttonProps 
}) => {
  const { canWrite, canAccess, isUnpaid, isSuspended, isPastDue } = useSubscriptionStatus();
  
  // Determine if button should be disabled
  let isDisabled = disabled; // Start with prop value
  let tooltipMessage = null;
  
  if (requireWrite && !canWrite) {
    isDisabled = true;
    if (isUnpaid) {
      tooltipMessage = "Ihr Account ist im Read-Only Modus. Bitte aktualisieren Sie Ihre Zahlungsmethode.";
    } else if (isSuspended) {
      tooltipMessage = "Ihr Account wurde gesperrt. Bitte aktualisieren Sie Ihre Zahlungsmethode.";
    }
  }
  
  if (requireAccess && !canAccess) {
    isDisabled = true;
    tooltipMessage = "Ihr Account wurde gesperrt. Bitte aktualisieren Sie Ihre Zahlungsmethode.";
  }
  
  // Handle click with guard
  const handleClick = (e) => {
    if (!canWrite && requireWrite) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (!canAccess && requireAccess) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };
  
  // If no tooltip needed, just return button
  if (!tooltipMessage) {
    return (
      <Button
        {...buttonProps}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    );
  }
  
  // Return button with tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button
              {...buttonProps}
              disabled={isDisabled}
              onClick={handleClick}
              className={`${buttonProps.className || ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{tooltipMessage}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
